import { Button, Checkbox, InputNumber, Popconfirm } from "@arco-design/web-react";
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

import { HorizontalScrollRegion } from "../../../shared/ui/HorizontalScrollRegion";
import { makeLineComparator } from "../config/quoteBudgetConfig";
import {
  buildLineFormula,
  applyInlineFormula,
  buildBusFuelFormula,
  busFuelRegionFromBasis,
  formatMoney,
  formatLineAmountForDisplay,
  formatLineFormulaForDisplay,
  formatLineGroupTotalForDisplay,
  filterVisibleLines,
  inlineFormulaValidationMessage,
  parseBusFuelRows,
  toggleAllCollapsed,
  visibleLineCount,
  type BusFuelRow,
  type OperationLogEntry,
} from "../service/quoteBudgetEditorViewService";
import {
  busFuelEffectiveRateText,
  busFuelRegionAddon,
  calculateBusBaseFeeAmount,
  calculateBusFuelMoney,
  isBusinessTaxLine,
  normalizeMoneyPlain,
} from "../service/quoteBudgetCalculationService";
import type { QuoteBudgetLine } from "../types";

/** 分组展示顺序；当前类置顶，其余按此表，未列出的追加在后. */
const RESOURCE_GROUP_ORDER = ["住宿", "大交通", "包车", "考察点", "餐", "导游", "杂费", "营业税"];

/** 营业税仅在展示层独立成组，持久化资源类型仍保持“杂费”。 */
function displayGroupKey(line: QuoteBudgetLine): string {
  return isBusinessTaxLine(line) ? "营业税" : line.resourceType;
}

/** 首次进入或切换场景时只展开当前场景。 */
function focusCollapsedState(keys: string[], activeType: string | undefined): Record<string, boolean> {
  if (!activeType) return Object.fromEntries(keys.map((key) => [key, false]));
  return Object.fromEntries(keys.map((key) => [key, key !== activeType]));
}

type BusBaseFeeEntry = {
  seat: number;
  mappedSeat: number;
  count: number;
  price: string;
};

type BusBaseFeeValidationError = {
  index: number;
  message: string;
};

const BUS_DAILY_FIXED_SEAT_MAPPING: Record<number, number> = {
  37: 39,
  47: 49,
  48: 49,
  53: 55,
};

/** 明细面板属性。明细只读展示，行内公式变更通过 onApplyFormula 上抛，删除通过 onRemoveLine. */
export type QuoteBudgetDetailPanelProps = {
  lines: QuoteBudgetLine[];
  recentLineId?: string | null;
  recentLineIds?: string[];
  /** 行程考察点名称顺序，用于把同一点位的明细连续展示. */
  pointOrder?: readonly string[];
  logs?: OperationLogEntry[];
  /** 当前录入场景资源类型，用于只看当前类过滤与置顶. */
  activeType?: string;
  onlyCurrent?: boolean;
  onOnlyCurrentChange?: (checked: boolean) => void;
  hideToolbarFilters?: boolean;
  readonly?: boolean;
  /** 权威状态已禁止写入时保留局部草稿供取消，但不得再应用。 */
  commitDisabled?: boolean;
  onApplyFormula?: (lineId: string, formulaText: string) => void;
  onPendingDraftChange?: (pending: boolean) => void;
  onRemoveLine?: (lineId: string) => void;
  onRemoveType?: (resourceType: string) => void;
  riskReasonByLineId?: Record<string, string>;
};

/** 计算提示文案：按行单位映射录入口径（原型 formulaInputHint）. */
function formulaInputHint(line: QuoteBudgetLine): string {
  switch (line.unit) {
    case "人晚":
      return "人数 x 晚数 x 单价";
    case "人餐":
      return "人数 x 餐数 x 单价";
    case "人次":
      return line.resourceType === "大交通" && line.resourceEntryType === "手续费"
        ? "人数 x 次数 x 单价"
        : "人数 x 单价";
    case "张":
      return "人数 x 次数 x 单价";
    case "人天":
      return "人数 x 天数 x 单价";
    case "车天":
      return "车辆数 x 包干单价";
    case "车趟":
      return "车趟数 x 整段包干单价";
    case "辆":
      return "车辆数 x 整段包干单价";
    case "人项":
      return "人项数 x 单价";
    case "人":
      return "人数 x 单价";
    case "项":
      return "项目数 x 单价";
    case "次":
      return "次数 x 单价";
    case "晚":
      return "晚数 x 单价";
    case "天":
      return "天数 x 单价";
    case "餐":
      return "餐数 x 单价";
    default:
      return "数量 x 单价";
  }
}

/** 按资源类型分组并排序分组键：当前类优先，再按固定顺序，未列出追加在后. */
function groupKeys(lines: QuoteBudgetLine[], activeType: string | undefined): string[] {
  const keys = [...new Set(lines.map(displayGroupKey))];
  return keys.sort((a, b) => {
    if (a === activeType) return -1;
    if (b === activeType) return 1;
    const ia = RESOURCE_GROUP_ORDER.indexOf(a);
    const ib = RESOURCE_GROUP_ORDER.indexOf(b);
    return (ia < 0 ? RESOURCE_GROUP_ORDER.length : ia) - (ib < 0 ? RESOURCE_GROUP_ORDER.length : ib);
  });
}

/** 是否为按天计一次的包车固定费行。 */
function isBusDailyFixedLine(line: QuoteBudgetLine): boolean {
  return line.resourceType === "包车" && line.resourceEntryType === "包车固定费";
}

/** 金额转为公式输入中的简洁数字串。 */
function plainMoney(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "";
  return normalizeMoneyPlain(value) ?? "";
}

const NON_NEGATIVE_DECIMAL = /^\d+(?:\.\d+)?$/;

/** 校验统一基础费原始输入，避免非法文本被归一化为零。 */
function busBaseFeeValidationError(
  entries: BusBaseFeeEntry[],
  rows: QuoteBudgetLine[],
): BusBaseFeeValidationError | null {
  for (const [index, entry] of entries.entries()) {
    const price = entry.price.trim();
    if (!price) return { index, message: "基础费不能为空，请输入非负十进制金额" };
    if (!NON_NEGATIVE_DECIMAL.test(price)) {
      return { index, message: "基础费仅支持非负十进制金额，不支持科学计数法" };
    }
    if (calculateBusBaseFeeAmount([{ price, count: 1 }]) === null) {
      return { index, message: "基础费超出可安全计算范围" };
    }
  }
  if (calculateBusBaseFeeAmount(entries) === null) {
    return { index: 0, message: "基础费合计超出可安全计算范围" };
  }
  for (const line of rows) {
    const dayCount = Math.max(1, Number(line.dayCount ?? line.quantity) || 1);
    if (calculateBusBaseFeeAmount(entries, dayCount) === null) {
      return { index: 0, message: "基础费总额超出可安全计算范围" };
    }
  }
  return null;
}

/** 包车燃油编辑值必须是有限非负数，且合计可安全按两位小数计算。 */
function busFuelValidationMessage(
  rows: BusFuelRow[],
  region: "regular" | "sanzhou" = "regular",
  tollAmount: string | number = 0,
): string | null {
  if (rows.some((row) => !NON_NEGATIVE_DECIMAL.test(String(row.regularRate).trim()))) {
    return "常规油耗不能为空，且必须为非负数";
  }
  return calculateBusFuelMoney(rows, region, tollAmount) === null
    ? "包车燃油金额超出可安全计算范围"
    : null;
}

/** 读取固定费行里的车型数量，跨多日重复行只保留同车型首次数量。 */
function busBaseFeeVehicles(rows: QuoteBudgetLine[]): Array<{ seat: number; count: number }> {
  const vehicles = new Map<number, number>();
  for (const line of rows) {
    for (const match of String(line.itemSpecSnapshot ?? "").matchAll(/(\d+)座\s*x\s*(\d+)辆/g)) {
      const seat = Number(match[1]);
      const count = Number(match[2]);
      if (seat > 0 && count > 0 && !vehicles.has(seat)) {
        vehicles.set(seat, count);
      }
    }
  }
  return Array.from(vehicles, ([seat, count]) => ({ seat, count }));
}

/** 读取固定费依据里的车型、数量和单价，用于打开弹窗时预填。 */
function busBaseFeeEntriesBySeat(rows: QuoteBudgetLine[]): Map<number, Omit<BusBaseFeeEntry, "seat">> {
  const entries = new Map<number, Omit<BusBaseFeeEntry, "seat">>();
  for (const line of rows) {
    for (const match of String(line.priceBasisDesc ?? "").matchAll(/(\d+)座(?:按(\d+)座)?[^；]*?(\d+(?:\.\d+)?)元\/天\s*x\s*(\d+)辆/g)) {
      const seat = Number(match[1]);
      if (seat > 0 && !entries.has(seat)) {
        entries.set(seat, {
          mappedSeat: Number(match[2] ?? match[1]),
          count: Number(match[4]) || 1,
          price: plainMoney(match[3]),
        });
      }
    }
  }
  return entries;
}

/** 根据固定费行构造弹窗内的车型基础费输入项。 */
function buildBusBaseFeeEntries(rows: QuoteBudgetLine[]): BusBaseFeeEntry[] {
  const entriesBySeat = busBaseFeeEntriesBySeat(rows);
  if (entriesBySeat.size > 0) {
    return Array.from(entriesBySeat, ([seat, entry]) => ({ seat, ...entry }));
  }
  const vehicles = busBaseFeeVehicles(rows);
  if (vehicles.length === 0) {
    return [
      {
        seat: 0,
        mappedSeat: 0,
        count: Number(rows[0]?.quantity) || 1,
        price: plainMoney(rows[0]?.budgetUnitPrice),
      },
    ];
  }
  return vehicles.map(({ seat, count }) => {
    return {
      seat,
      mappedSeat: BUS_DAILY_FIXED_SEAT_MAPPING[seat] ?? seat,
      count,
      price: vehicles.length === 1 ? plainMoney(rows[0]?.budgetUnitPrice) : "",
    };
  });
}

/** 从固定费计价依据中保留月份展示，用户改价后仍能追溯月份口径。 */
function busFixedMonth(line: QuoteBudgetLine): string {
  const month = String(line.priceBasisDesc ?? "").match(/(?:^|\D)(1[0-2]|[1-9])月/)?.[1];
  return month ? `${month}月 ` : "";
}

/** 生成固定费行内公式，汇总车型输入后仍只回写当前固定费行。 */
function buildBusBaseFeeFormula(line: QuoteBudgetLine, entries: BusBaseFeeEntry[]): string {
  const dayCount = Math.max(1, Number(line.dayCount ?? line.quantity) || 1);
  const amount = calculateBusBaseFeeAmount(entries, dayCount) ?? "0.00";
  const month = busFixedMonth(line);
  const details = entries.map((entry) => {
    const price = plainMoney(entry.price) || "0";
    if (entry.seat <= 0) {
      return `${price}元/天 x ${entry.count}辆`;
    }
    const mapped = entry.mappedSeat !== entry.seat ? `按${entry.mappedSeat}座` : "";
    return `${entry.seat}座${mapped} ${month}${price}元/天 x ${entry.count}辆`;
  });
  const days = dayCount > 1 ? `用车天数=${dayCount}；` : "";
  return `包车固定费=${amount}；${days}${details.join("；")}`;
}

/** 生成弹窗输入框标签，按实际车型展示每日基础费。 */
function busBaseFeeInputLabel(entry: BusBaseFeeEntry): string {
  return entry.seat > 0 ? `${entry.seat}座每日基础费` : "每日基础费";
}

/** 拆分包车燃油计价摘要与可展开的原始大巴腿. */
function busFuelDetail(basis: string): { summary: string; detailBasis: string; segments: string[] } {
  const marker = "原始大巴线段：";
  const markerIndex = basis.indexOf(marker);
  if (markerIndex < 0) return { summary: basis, detailBasis: "", segments: [] };
  const rawSegments = basis.slice(markerIndex + marker.length);
  return {
    summary: basis.slice(0, markerIndex).replace(/；$/, ""),
    detailBasis: `${marker}${rawSegments}`,
    segments: rawSegments.split("；").filter(Boolean),
  };
}

/** 从包车规格读取已归并的高速费，缺失按 0. */
function busTollAmount(line: QuoteBudgetLine): string {
  return String(line.itemSpecSnapshot ?? "").match(/高速费(\d+(?:\.\d+)?)元/)?.[1] ?? "";
}

/** 生成明细行可见名称，空快照回退资源类型。 */
function detailDisplayName(line: QuoteBudgetLine): string {
  return String(line.itemNameSnapshot ?? "").trim()
    || String(line.resourceType ?? "").trim()
    || "未命名项目";
}

/** 生成去除首尾空白的角色名称。 */
function detailDisplayRole(line: QuoteBudgetLine): string {
  return String(line.travelerRole ?? "").trim();
}

/** 组合行操作的基础无障碍目标名称。 */
function detailActionTarget(line: QuoteBudgetLine): string {
  const displayName = detailDisplayName(line);
  const displayRole = detailDisplayRole(line);
  return displayRole ? `${displayName} ${displayRole}` : displayName;
}

/** 为全部可见明细中的同名同角色项追加序号，避免跨组操作仍产生重复名称。 */
function duplicateActionQualifiers(lines: QuoteBudgetLine[]): ReadonlyMap<string, string> {
  const targetCounts = new Map<string, number>();
  for (const line of lines) {
    const target = detailActionTarget(line);
    targetCounts.set(target, (targetCounts.get(target) ?? 0) + 1);
  }

  const targetOrdinals = new Map<string, number>();
  const qualifiers = new Map<string, string>();
  for (const line of lines) {
    const target = detailActionTarget(line);
    if ((targetCounts.get(target) ?? 0) < 2) continue;
    const ordinal = (targetOrdinals.get(target) ?? 0) + 1;
    targetOrdinals.set(target, ordinal);
    qualifiers.set(line.budgetLineId, `第 ${ordinal} 条`);
  }
  return qualifiers;
}

/** 单条明细行：名称按文本渲染（防 XSS）、角色标签、派生公式、金额、行内公式轻编辑与删除. */
function DetailRow(props: {
  line: QuoteBudgetLine;
  isRecent: boolean;
  editing: boolean;
  riskReasonByLineId?: Record<string, string>;
  accessibleQualifier?: string;
  editingLocked?: boolean;
  readonly?: boolean;
  commitDisabled?: boolean;
  onApplyFormula?: (lineId: string, formulaText: string) => void;
  onPendingDraftChange?: (pending: boolean) => void;
  onEditingChange: (lineId: string | null) => void;
  onRemoveLine?: (lineId: string) => void;
}) {
  const { line, isRecent, editing, readonly } = props;
  const [formula, setFormula] = useState("");
  const [formulaError, setFormulaError] = useState<string | null>(null);
  const formulaHelpId = useId();
  const formulaErrorId = useId();
  const [fuelRegion, setFuelRegion] = useState<"regular" | "sanzhou">("regular");
  const [fuelRows, setFuelRows] = useState<BusFuelRow[]>([]);
  const editTriggerRef = useRef<HTMLElement | null>(null);
  const editDialogRef = useRef<HTMLDivElement | null>(null);
  const wasEditingRef = useRef(false);
  const isBusFuelLine = line.resourceType === "包车" && parseBusFuelRows(line).length > 0;
  const busFuelBasis =
    line.resourceType === "包车" && line.resourceEntryType === "包车燃油费"
      ? line.priceBasisDesc?.trim()
      : "";
  const busFuelDetailValue = busFuelDetail(busFuelBasis ?? "");
  const tollAmount = busTollAmount(line);
  const displayName = detailDisplayName(line);
  const displayRole = detailDisplayRole(line);
  const displaySpec = line.resourceType === "考察点" && line.skuNameSnapshot?.trim()
    ? line.skuNameSnapshot.trim()
    : line.itemSpecSnapshot?.trim();
  const accessibleTargetBase = detailActionTarget(line);
  const accessibleTarget = props.accessibleQualifier
    ? `${accessibleTargetBase}，${props.accessibleQualifier}`
    : accessibleTargetBase;
  const riskReason = props.riskReasonByLineId?.[line.budgetLineId];
  const formulaPreview = editing && !isBusFuelLine && !inlineFormulaValidationMessage(line, formula)
    ? applyInlineFormula(line, formula) : null;

  /** 打开编辑：预填当前派生公式，便于在现值基础上微调（原型 formulaInputValue）. */
  function openEditor(trigger?: HTMLElement) {
    if (readonly || props.commitDisabled) return;
    editTriggerRef.current = trigger
      ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    setFormula(buildLineFormula(line));
    setFormulaError(null);
    setFuelRegion(busFuelRegionFromBasis(line));
    setFuelRows(parseBusFuelRows(line));
    props.onEditingChange(line.budgetLineId);
  }

  /** 关闭行内编辑并清除未提交的局部草稿状态。 */
  function closeEditor() {
    props.onPendingDraftChange?.(false);
    props.onEditingChange(null);
  }

  /** 确定：上抛原始公式文本，由上层解析并落行（原型 data-edit-save）. */
  function applyEditor() {
    if (readonly || props.commitDisabled) return;
    if (isBusFuelLine) {
      const validationMessage = busFuelValidationMessage(fuelRows, fuelRegion, tollAmount);
      if (validationMessage) {
        setFormulaError(validationMessage);
        return;
      }
    } else {
      const validationMessage = inlineFormulaValidationMessage(line, formula);
      if (validationMessage) {
        setFormulaError(validationMessage);
        return;
      }
    }
    props.onApplyFormula?.(
      line.budgetLineId,
      isBusFuelLine
        ? buildBusFuelFormula(fuelRows, fuelRegion, {
            tollAmount,
            segmentDetailBasis: busFuelDetailValue.detailBasis,
          })
        : formula,
    );
    closeEditor();
  }

  /** Enter 等价于点击确定. */
  /** 处理公式输入框的 Escape/Enter 快捷键。 */
  function handleInputKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeEditor();
      return;
    }
    if (event.key === "Enter") applyEditor();
  }

  /** 允许从浮层内任意控件按 Escape 返回触发按钮。 */
  function handleDialogKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key !== "Escape") return;
    event.preventDefault();
    closeEditor();
  }

  useEffect(() => {
    if (editing) {
      editDialogRef.current?.querySelector<HTMLElement>("input, button")?.focus();
      wasEditingRef.current = true;
      return;
    }
    if (!wasEditingRef.current) return;
    wasEditingRef.current = false;
    editTriggerRef.current?.focus();
  }, [editing]);

  return (
    <div data-line-id={line.budgetLineId} className="quote-budget-detail-row">
      <span className="quote-budget-detail-name">
        <span className="quote-budget-detail-name-line">
          <span>{displayName}</span>
          {isRecent ? <span className="quote-budget-recent-badge">新加入</span> : null}
        </span>
        {displaySpec ? (
          <span className="quote-budget-detail-spec">{displaySpec}</span>
        ) : null}
        {busFuelDetailValue.summary ? (
          <span className="quote-budget-detail-basis">{busFuelDetailValue.summary}</span>
        ) : null}
        {riskReason ? <span className="quote-budget-detail-risk-reason" role="note">待处理：{riskReason}</span> : null}
        {busFuelDetailValue.segments.length > 0 ? (
          <details className="quote-budget-bus-segments">
            <summary>原始大巴腿 {busFuelDetailValue.segments.length} 条</summary>
            <ul>
              {busFuelDetailValue.segments.map((segment, index) => (
                <li key={`${line.budgetLineId}-segment-${index}`}>{segment}</li>
              ))}
            </ul>
          </details>
        ) : null}
      </span>
      <span className="quote-budget-detail-role">
        {displayRole ? (
          <span className="quote-budget-role-tag" data-role={displayRole}>
            {displayRole}
          </span>
        ) : null}
      </span>
      <span className="quote-budget-detail-formula">
        {formatLineFormulaForDisplay(line)}
        {!isBusinessTaxLine(line) ? <small className="quote-budget-detail-mode">{line.resourceType === "包车" ? "包车数量：按车型及计价规则计算" : line.isLinkedToPeopleCount ? "人数联动：随左侧人数重算" : "固定数量：不随左侧人数变化"}</small> : null}
      </span>
      <span className="quote-budget-detail-amount">{formatLineAmountForDisplay(line)}</span>
      <span className="quote-budget-detail-actions">
        {readonly ? null : (
          <>
            <Button
              size="mini"
              aria-label={`修改 ${accessibleTarget}`}
              disabled={props.commitDisabled || (props.editingLocked && !editing)}
              onClick={(event) => (editing
                ? props.onEditingChange(null)
                : openEditor(event.currentTarget))}
            >
              改
            </Button>
            <Popconfirm
              title="确认删除该条明细吗？"
              content="删除后需保存草稿才会落库。"
              disabled={props.commitDisabled || props.editingLocked}
              okText="确认删除"
              cancelText="取消"
              okType="primary"
              okButtonProps={{ status: "danger" }}
              trigger="click"
              unmountOnExit
              autoFocus
              focusLock
              onOk={() => {
                if (!readonly && !props.commitDisabled) {
                  props.onRemoveLine?.(line.budgetLineId);
                }
              }}
            >
              <Button
                size="mini"
                status="danger"
                aria-label={`删除 ${accessibleTarget}`}
                disabled={props.commitDisabled || props.editingLocked}
              >
                删
              </Button>
            </Popconfirm>
          </>
        )}
        {/* 行内修改弹窗锚定在操作区左侧（原型 edit-popover），故置于 actions 内. */}
        {editing && !readonly ? (
          <div
            ref={editDialogRef}
            className={`quote-budget-edit-popover${isBusFuelLine ? " quote-budget-edit-popover--bus-fuel" : ""}`}
            role="dialog"
            aria-label="修改计算"
            tabIndex={-1}
            onKeyDown={handleDialogKeyDown}
          >
            <div className="quote-budget-edit-popover__title">
              {isBusFuelLine ? "包车燃油设置" : "修改计算"}
            </div>
            {isBusFuelLine ? (
              <BusFuelEditor
                rows={fuelRows}
                region={fuelRegion}
                tollAmount={tollAmount}
                onRegionChange={(region) => {
                  setFuelRegion(region);
                  props.onPendingDraftChange?.(true);
                }}
                onRowsChange={(rows) => {
                  setFuelRows(rows);
                  setFormulaError(null);
                  props.onPendingDraftChange?.(true);
                }}
              />
            ) : (
              <>
                <label className="quote-budget-edit-popover__field">
                  <span>计算公式</span>
                  <input
                    aria-label="公式输入"
                    aria-invalid={Boolean(formulaError)}
                    aria-describedby={`${formulaHelpId}${formulaError ? ` ${formulaErrorId}` : ""}`}
                    value={formula}
                    disabled={props.commitDisabled}
                    onChange={(event) => {
                      setFormula(event.target.value);
                      setFormulaError(null);
                      props.onPendingDraftChange?.(true);
                    }}
                    onKeyDown={handleInputKeyDown}
                  />
                </label>
                <div id={formulaHelpId} className="quote-budget-edit-popover__hint">
                  <p>{formulaInputHint(line)}</p>
                  <p>{line.resourceType === "包车" ? "保留完整包车公式及车型计价规则，不修改左侧总人数。" : "可完整修改分档数量与单价；明确输入数量后按固定数量保存，不修改左侧总人数。"}</p>
                  {formulaPreview ? <p role="status">应用后金额：{formatLineAmountForDisplay(formulaPreview)}；{line.resourceType === "包车" ? "保留包车计价规则" : formulaPreview.isLinkedToPeopleCount ? "保持人数联动" : "固定数量，不再随人数变化"}</p> : null}
                </div>
              </>
            )}
            {formulaError ? (
              <p id={formulaErrorId} className="quote-budget-edit-popover__error" role="alert">{formulaError}</p>
            ) : null}
            <div className="quote-budget-edit-popover__actions">
              <Button size="mini" onClick={closeEditor}>
                取消
              </Button>
              <Button size="mini" type="primary" disabled={props.commitDisabled} onClick={applyEditor}>
                确定
              </Button>
            </div>
          </div>
        ) : null}
      </span>
    </div>
  );
}

/** 包车燃油弹窗：按车型展示常规油耗，三州只叠加固定差额。 */
function BusFuelEditor(props: {
  rows: BusFuelRow[];
  region: "regular" | "sanzhou";
  tollAmount?: string | number;
  onRegionChange: (region: "regular" | "sanzhou") => void;
  onRowsChange: (rows: BusFuelRow[]) => void;
}) {
  const hasInvalidFuelRate = Boolean(
    busFuelValidationMessage(props.rows, props.region, props.tollAmount),
  );
  const amounts = hasInvalidFuelRate
    ? null
    : calculateBusFuelMoney(props.rows, props.region, props.tollAmount ?? 0);
  return (
    <div className="quote-budget-bus-fuel-editor">
      <div className="quote-budget-bus-fuel-region" aria-label="油耗地区">
        <Button
          size="mini"
          type={props.region === "regular" ? "primary" : "secondary"}
          aria-pressed={props.region === "regular"}
          onClick={() => props.onRegionChange("regular")}
        >
          常规
        </Button>
        <Button
          size="mini"
          type={props.region === "sanzhou" ? "primary" : "secondary"}
          aria-pressed={props.region === "sanzhou"}
          onClick={() => props.onRegionChange("sanzhou")}
        >
          三州
        </Button>
      </div>
      <p className="quote-budget-scroll-hint">表格可左右滑动查看全部列</p>
      <HorizontalScrollRegion
        className="quote-budget-bus-fuel-table"
        aria-label="包车燃油参数表，可横向滚动查看全部列"
      >
        <div className="quote-budget-bus-fuel-head">
          <span>车型</span>
          <span>数量</span>
          <span>公里</span>
          <span>常规油耗</span>
          <span>加价</span>
          <span>小计</span>
        </div>
        {props.rows.map((row, index) => {
          const effectiveRate = busFuelEffectiveRateText(row, props.region);
          const subtotal = calculateBusFuelMoney([row], props.region)?.fuelAmount ?? null;
          const hasValidRate = effectiveRate !== null && subtotal !== null;
          const addon = busFuelRegionAddon(row, props.region);
          return (
            <div key={`${row.seat}-${index}`} className="quote-budget-bus-fuel-row">
              <span>{row.seat}座</span>
              <span>{row.count}辆</span>
              <span>{row.distanceKm.toFixed(2)}km</span>
              <label>
                <span className="sr-only">{row.seat}座常规油耗</span>
                <InputNumber
                  aria-label={`${row.seat}座常规油耗`}
                  size="mini"
                  min={0}
                  step={0.1}
                  precision={2}
                  strictMode
                  value={String(row.regularRate)}
                  onChange={(value) => {
                    const regularRate = String(value ?? "");
                    props.onRowsChange(
                      props.rows.map((item, itemIndex) =>
                        itemIndex === index
                          ? { ...item, regularRate }
                          : item,
                      ),
                    );
                  }}
                />
              </label>
              <span>{hasValidRate ? addon ? `+${addon}` : "+0" : "待填写"}</span>
              <strong>{subtotal === null ? "待填写" : formatFuelMoney(subtotal)}</strong>
            </div>
          );
        })}
      </HorizontalScrollRegion>
      <div className="quote-budget-bus-fuel-total">
        {!amounts
          ? "待填写"
          : amounts.tollAmount !== "0.00"
          ? `燃油 ${formatFuelMoney(amounts.fuelAmount)} + 高速 ${formatFuelMoney(amounts.tollAmount)} = 合计 ${formatFuelMoney(amounts.totalAmount)}`
          : `合计 ${formatFuelMoney(amounts.totalAmount)}`}
      </div>
    </div>
  );
}

/** 弹窗内金额展示：跟明细金额一样取整显示，保存仍保留两位小数。 */
function formatFuelMoney(value: string | number): string {
  return formatMoney(value);
}

/**
 * 核价明细面板：分组排序、批量折叠、只看当前类、新加入高亮、行内公式与操作日志。
 * 折叠/过滤为本地 UI 态，不入库（QB-PU-001/010/013/021/023/024/027）。
 */
export function QuoteBudgetDetailPanel(props: QuoteBudgetDetailPanelProps) {
  const { lines, recentLineId, recentLineIds = [], logs = [], activeType, readonly, pointOrder } = props;
  const recentIds = useMemo(
    () => new Set(recentLineIds.length ? recentLineIds : recentLineId ? [recentLineId] : []),
    [recentLineId, recentLineIds],
  );
  const initialKeys = groupKeys(lines, activeType);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(
    () => focusCollapsedState(initialKeys, activeType),
  );
  const [innerOnlyCurrent, setInnerOnlyCurrent] = useState(false);
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [busBaseFeeGroup, setBusBaseFeeGroup] = useState<string | null>(null);
  const [busBaseFeeEntries, setBusBaseFeeEntries] = useState<BusBaseFeeEntry[]>([]);
  const [busBaseFeeError, setBusBaseFeeError] = useState<BusBaseFeeValidationError | null>(null);
  const [pendingDraft, setPendingDraft] = useState(false);
  const busBaseFeeTriggerRef = useRef<HTMLElement | null>(null);
  const busBaseFeeDialogRef = useRef<HTMLDivElement | null>(null);
  const previousBusBaseFeeGroupRef = useRef<string | null>(null);
  const previousActiveTypeRef = useRef(activeType);
  const onlyCurrent = props.onlyCurrent ?? innerOnlyCurrent;

  const visible = useMemo(
    () => filterVisibleLines(lines, { onlyCurrent, activeType: activeType ?? "" }),
    [lines, onlyCurrent, activeType],
  );
  const keys = useMemo(() => groupKeys(visible, activeType), [visible, activeType]);
  const actionQualifiers = useMemo(() => duplicateActionQualifiers(visible), [visible]);
  const comparator = useMemo(() => makeLineComparator(recentLineId, pointOrder), [pointOrder, recentLineId]);
  const openBusBaseFeeRows = useMemo(() => {
    if (!busBaseFeeGroup) return [];
    return visible
      .filter((line) => line.resourceType === busBaseFeeGroup && isBusDailyFixedLine(line))
      .sort(comparator);
  }, [busBaseFeeGroup, comparator, visible]);

  const allCollapsed = keys.length > 0 && keys.every((key) => collapsed[key]);
  const toggleLabel = allCollapsed ? "展开全部" : "收起全部";

  /** 一键展开/收起全部分组. */
  function handleToggleAll() {
    setCollapsed((prev) => toggleAllCollapsed(prev, keys).collapsed);
  }

  /** 当前类过滤可由外部场景区控制；未受控时保留面板本地状态. */
  function handleOnlyCurrentChange(checked: boolean) {
    props.onOnlyCurrentChange?.(checked);
    if (props.onlyCurrent === undefined) {
      setInnerOnlyCurrent(checked);
    }
  }

  /** 打开包车每日基础费批量编辑浮层。 */
  function openBusBaseFeeEditor(key: string, rows: QuoteBudgetLine[], trigger?: HTMLElement) {
    if (pendingDraft || props.commitDisabled) return;
    busBaseFeeTriggerRef.current = trigger
      ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    setEditingLineId(null);
    setBusBaseFeeEntries(buildBusBaseFeeEntries(rows));
    setBusBaseFeeError(null);
    setBusBaseFeeGroup(key);
  }

  /** 关闭统一基础费浮层并恢复触发控件焦点。 */
  function handleBusBaseFeeKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key !== "Escape") return;
    event.preventDefault();
    setPendingDraft(false);
    setBusBaseFeeError(null);
    setBusBaseFeeGroup(null);
  }

  /** 同一时刻只允许一个局部草稿，已有修改必须先取消或应用。 */
  function handleEditingChange(lineId: string | null) {
    if (lineId && pendingDraft && lineId !== editingLineId) return;
    if (lineId && busBaseFeeGroup) return;
    if (lineId === null) setPendingDraft(false);
    setEditingLineId(lineId);
  }

  /** 把统一基础费回写到每日固定费行，路线段燃油费不参与。 */
  function applyBusBaseFee(rows: QuoteBudgetLine[]) {
    if (readonly || props.commitDisabled) return;
    const validationError = busBaseFeeValidationError(busBaseFeeEntries, rows);
    if (validationError) {
      setBusBaseFeeError(validationError);
      return;
    }
    const entries = busBaseFeeEntries.map((entry) => ({ ...entry, price: plainMoney(entry.price) }));
    for (const line of rows) {
      props.onApplyFormula?.(line.budgetLineId, buildBusBaseFeeFormula(line, entries));
    }
    setPendingDraft(false);
    setBusBaseFeeGroup(null);
  }

  useEffect(() => {
    if (previousActiveTypeRef.current === activeType) return;
    previousActiveTypeRef.current = activeType;
    setCollapsed(focusCollapsedState(keys, activeType));
  }, [activeType, keys]);

  useEffect(() => {
    setCollapsed((prev) => Object.fromEntries(
      keys.map((key) => [key, prev[key] ?? Boolean(activeType && key !== activeType)]),
    ));
  }, [activeType, keys]);

  useEffect(() => {
    if (!readonly) return;
    setEditingLineId(null);
    setBusBaseFeeGroup(null);
    setBusBaseFeeEntries([]);
    setPendingDraft(false);
  }, [readonly]);

  useEffect(() => {
    if (!editingLineId) return;
    const target = visible.find((line) => line.budgetLineId === editingLineId);
    if (target && !target.isLocked) return;
    setEditingLineId(null);
    setPendingDraft(false);
  }, [editingLineId, visible]);

  useEffect(() => {
    if (!busBaseFeeGroup || openBusBaseFeeRows.length > 0) return;
    setBusBaseFeeGroup(null);
    setBusBaseFeeEntries([]);
    setBusBaseFeeError(null);
    setPendingDraft(false);
  }, [busBaseFeeGroup, openBusBaseFeeRows]);

  useEffect(() => {
    if (!busBaseFeeGroup || pendingDraft) return;
    setBusBaseFeeEntries(buildBusBaseFeeEntries(openBusBaseFeeRows));
  }, [busBaseFeeGroup, openBusBaseFeeRows, pendingDraft]);

  useEffect(() => {
    if (busBaseFeeGroup) {
      busBaseFeeDialogRef.current?.querySelector<HTMLElement>("input, button")?.focus();
      previousBusBaseFeeGroupRef.current = busBaseFeeGroup;
      return;
    }
    if (!previousBusBaseFeeGroupRef.current) return;
    previousBusBaseFeeGroupRef.current = null;
    busBaseFeeTriggerRef.current?.focus();
  }, [busBaseFeeGroup]);

  useEffect(() => {
    props.onPendingDraftChange?.(pendingDraft);
  }, [pendingDraft, props.onPendingDraftChange]);

  return (
    <section className="quote-budget-detail" aria-label="核价明细面板">
      <div className="quote-budget-detail-toolbar">
        {props.hideToolbarFilters ? null : (
          <Checkbox checked={onlyCurrent} disabled={pendingDraft} onChange={handleOnlyCurrentChange}>
            只看当前类
          </Checkbox>
        )}
        <span data-testid="visible-count">{visibleLineCount(visible)} 条</span>
        <Button
          className="quote-budget-detail-toolbar__toggle"
          size="small"
          disabled={pendingDraft}
          onClick={handleToggleAll}
        >
          {toggleLabel}
        </Button>
      </div>

      {visible.length === 0 ? (
        <div className="quote-budget-detail-empty" role="status">
          {readonly
            ? "当前核价版本暂无明细。"
            : onlyCurrent
              ? "当前分类暂无核价明细，请在上方录入并加入明细。"
              : "暂无核价明细，请在上方录入并加入明细。"}
        </div>
      ) : null}

      {keys.map((key) => {
        const rows = visible.filter((line) => displayGroupKey(line) === key).sort(comparator);
        const busDailyFixedRows = rows.filter(isBusDailyFixedLine);
        const canEditBusBaseFee = !readonly && busDailyFixedRows.length > 0;
        const canRemoveGroup = key !== "营业税" && Boolean(props.onRemoveType);
        const isCollapsed = Boolean(collapsed[key]);
        return (
          <div key={key} className="quote-budget-detail-group" data-type={key}>
            <button
              type="button"
              className="quote-budget-detail-group-head"
              aria-expanded={!isCollapsed}
              disabled={pendingDraft}
              onClick={() => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }))}
            >
              <span>{key}</span>
              <span className="quote-budget-detail-group-count">{rows.length} 行</span>
              <span className="quote-budget-detail-group-total">{formatLineGroupTotalForDisplay(rows)}</span>
              <span className="quote-budget-detail-group-toggle">{isCollapsed ? "展开" : "收起"}</span>
            </button>
            {!isCollapsed && !readonly && (canEditBusBaseFee || canRemoveGroup) ? (
              <div className="quote-budget-detail-group-tools">
                {canEditBusBaseFee ? (
                  <Button
                    size="mini"
                    disabled={pendingDraft || props.commitDisabled}
                    onClick={(event) => openBusBaseFeeEditor(key, busDailyFixedRows, event.currentTarget)}
                  >
                    统一基础费
                  </Button>
                ) : null}
                {canRemoveGroup ? (
                  <Popconfirm
                    title={`确认删除${key}的全部 ${rows.length} 行明细吗？`}
                    content="删除后需保存草稿才会落库。"
                    disabled={pendingDraft || props.commitDisabled}
                    okText="确认删除"
                    cancelText="取消"
                    okType="primary"
                    okButtonProps={{ status: "danger" }}
                    trigger="click"
                    unmountOnExit
                    autoFocus
                    focusLock
                    onOk={() => {
                      if (!readonly && !props.commitDisabled) {
                        props.onRemoveType?.(key);
                      }
                    }}
                  >
                    <Button size="mini" status="danger" disabled={pendingDraft || props.commitDisabled}>删除整类</Button>
                  </Popconfirm>
                ) : null}
                {canEditBusBaseFee && busBaseFeeGroup === key ? (
                  <div
                    ref={busBaseFeeDialogRef}
                    className="quote-budget-bus-base-popover"
                    role="dialog"
                    aria-label="统一用车基础费"
                    tabIndex={-1}
                    onKeyDown={handleBusBaseFeeKeyDown}
                  >
                    <div className="quote-budget-edit-popover__title">统一用车基础费</div>
                    {busBaseFeeEntries.map((entry, index) => {
                      const label = busBaseFeeInputLabel(entry);
                      return (
                        <label
                          key={`${entry.seat}-${index}`}
                          className="quote-budget-edit-popover__field"
                        >
                          <span>{label}</span>
                          <input
                            aria-label={label}
                            aria-describedby={busBaseFeeError?.index === index ? "bus-base-fee-error" : undefined}
                            aria-invalid={busBaseFeeError?.index === index}
                            disabled={props.commitDisabled}
                            value={entry.price}
                            onChange={(event) => {
                              const price = event.target.value;
                              setBusBaseFeeEntries((prev) =>
                                prev.map((item, itemIndex) =>
                                  itemIndex === index ? { ...item, price } : item,
                                ),
                              );
                              setBusBaseFeeError(null);
                              setPendingDraft(true);
                            }}
                          />
                        </label>
                      );
                    })}
                    {busBaseFeeError ? (
                      <p id="bus-base-fee-error" className="quote-budget-edit-popover__error" role="alert">
                        {busBaseFeeError.message}
                      </p>
                    ) : null}
                    <div className="quote-budget-edit-popover__actions">
                      <Button size="mini" onClick={() => {
                        setPendingDraft(false);
                        setBusBaseFeeError(null);
                        setBusBaseFeeGroup(null);
                      }}>
                        取消
                      </Button>
                      <Button
                        size="mini"
                        type="primary"
                        disabled={props.commitDisabled}
                        onClick={() => applyBusBaseFee(busDailyFixedRows)}
                      >
                        应用
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
            {isCollapsed
              ? null
              : rows.map((line) => (
                  <DetailRow
                    riskReasonByLineId={props.riskReasonByLineId}
                    key={line.budgetLineId}
                    line={line}
                    isRecent={recentIds.has(line.budgetLineId)}
                    editing={editingLineId === line.budgetLineId}
                    accessibleQualifier={actionQualifiers.get(line.budgetLineId)}
                    editingLocked={pendingDraft || Boolean(busBaseFeeGroup)}
                    readonly={readonly || line.isLocked}
                    commitDisabled={props.commitDisabled}
                    onApplyFormula={props.onApplyFormula}
                    onEditingChange={handleEditingChange}
                    onPendingDraftChange={setPendingDraft}
                    onRemoveLine={props.onRemoveLine}
                  />
                ))}
          </div>
        );
      })}

      {logs.length > 0 ? (
        <div className="quote-budget-operation-log" aria-label="操作日志">
          {logs.map((entry, index) => (
            <div key={`${entry.time}-${index}`} data-testid="operation-log-item" className="quote-budget-operation-log-item">
              <span>{entry.time}</span> <span>{entry.message}</span>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}
